import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import * as Collapsible from '@radix-ui/react-collapsible'
import { X, ChevronDown, Sparkles, LogOut } from 'lucide-react'
import { DarkModeToggle } from '../DarkModeToggle'
import { useAuthz } from '../../auth/useAuthz'
import { useWireAuth } from '../../auth/useWireAuth'
import { useTopics, useRegions } from '../../hooks/useTaxonomy'
import { useLiveEditions } from '../../api/hooks/usePages'
import wLogoClean from '../../assets/w_logo_clean.svg'
import wLogoDark from '../../assets/w_logo_dark.svg'

// ── Accordion ────────────────────────────────────────────────────────────────

interface AccordionSectionProps {
  heading: string
  children: React.ReactNode
}

function AccordionSection({ heading, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className="border-b border-line">
      <Collapsible.Trigger className="w-full flex items-center justify-between px-5 py-3.5 text-[10px] font-semibold tracking-widest text-fg-secondary uppercase">
        {heading}
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={`transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`}
        />
      </Collapsible.Trigger>
      <Collapsible.Content className="px-5 pb-4 flex flex-col gap-2.5">
        {children}
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

// ── Sheet content ─────────────────────────────────────────────────────────────

interface SheetContentProps {
  onClose: () => void
}

function SheetContent({ onClose }: SheetContentProps) {
  const navigate = useNavigate()
  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()
  const { data: editions = [] } = useLiveEditions()
  const { isWorkflowMember } = useAuthz()
  const { user, logout } = useWireAuth()

  const displayName = user?.given_name ?? user?.name ?? user?.email ?? 'User'

  function navTo(path: string) {
    navigate(path)
    onClose()
  }

  const sortedTopics   = [...topics].sort((a, b) => a.label.localeCompare(b.label))
  const sortedRegions  = [...regions].sort((a, b) => a.label.localeCompare(b.label))
  const sortedEditions = [...editions].sort((a, b) => a.title.localeCompare(b.title))

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-line-strong">
        <Link to="/wire" onClick={onClose} aria-label="Wire home">
          <img src={wLogoClean} alt="Wire" className="h-7 w-auto dark:hidden" />
          <img src={wLogoDark} alt="" aria-hidden="true" className="hidden h-7 w-auto dark:block" />
        </Link>
        <Dialog.Close
          className="p-1.5 text-fg-secondary hover:text-fg transition-colors"
          aria-label="Close menu"
        >
          <X size={18} />
        </Dialog.Close>
      </div>

      {/* Quick nav */}
      <div className="px-5 py-3 flex flex-col gap-0.5 border-b border-line">
        <button
          onClick={() => navTo('/wire/search')}
          className="flex items-center gap-2.5 py-2.5 text-sm text-fg-secondary hover:text-fg transition-colors text-left"
        >
          <Sparkles size={14} aria-hidden="true" />
          HotWIRe AI
        </button>
        <button
          onClick={() => navTo('/wire/me')}
          className="py-2.5 text-sm text-fg-secondary hover:text-fg transition-colors text-left"
        >
          My Wire
        </button>
        {isWorkflowMember && (
          <button
            onClick={() => navTo('/admin')}
            className="py-2.5 text-sm text-fg-secondary hover:text-fg transition-colors text-left"
          >
            Admin
          </button>
        )}
      </div>

      {/* Taxonomy */}
      <AccordionSection heading="Topics">
        {sortedTopics.map((t) => (
          <button
            key={t.slug}
            onClick={() => navTo(`/wire/topics/${t.slug}`)}
            className="text-sm text-fg-secondary hover:text-fg transition-colors text-left"
          >
            {t.label}
          </button>
        ))}
      </AccordionSection>

      <AccordionSection heading="Regions">
        {sortedRegions.map((r) => (
          <button
            key={r.slug}
            onClick={() => navTo(`/wire/regions/${r.slug}`)}
            className="text-sm text-fg-secondary hover:text-fg transition-colors text-left"
          >
            {r.label}
          </button>
        ))}
      </AccordionSection>

      {editions.length > 0 && (
        <AccordionSection heading="Editions">
          {sortedEditions.map((e) => (
            <button
              key={e.slug}
              onClick={() => navTo(`/wire/editions/${e.slug}`)}
              className="text-sm text-fg-secondary hover:text-fg transition-colors text-left"
            >
              {e.title}
            </button>
          ))}
        </AccordionSection>
      )}

      {/* Footer */}
      <div className="mt-auto border-t border-line px-5 py-4 flex items-center justify-between">
        <span className="text-xs text-fg-secondary truncate mr-3">{displayName}</span>
        <div className="flex items-center gap-3 shrink-0">
          <DarkModeToggle />
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-xs text-fg-secondary hover:text-fg transition-colors"
            aria-label="Sign out"
          >
            <LogOut size={13} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Public component ──────────────────────────────────────────────────────────

interface NavbarMobileSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NavbarMobileSheet({ open, onOpenChange }: NavbarMobileSheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 lg:hidden" />
        <Dialog.Content
          className="fixed inset-y-0 left-0 z-50 w-72 bg-canvas border-r border-line shadow-xl overflow-y-auto flex flex-col outline-none lg:hidden"
        >
          <Dialog.Title className="sr-only">Navigation menu</Dialog.Title>
          <SheetContent onClose={() => onOpenChange(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
